import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { RealtorProvider, useRealtorContext } from './context/RealtorContext';
import ErrorBoundary from './components/ErrorBoundary';
import DebugInfo from './components/DebugInfo';
import { Toaster } from 'react-hot-toast'; // Import Toaster
import './App.css';

// Import all pages
import RealtorOnboardingPage from './pages/RealtorOnboardingPage';
import HomePage from './pages/HomePage';
import RealtorDashboardPage from './pages/RealtorDashboardPage';
import AffiliateOnboardingPage from './pages/AffiliateOnboardingPage';
import AffiliateManagementPage from './pages/AffiliateManagementPage';
import InviteAffiliatePage from './pages/InviteAffiliatePage';
import AffiliateRegisterPage from './pages/AffiliateRegisterPage';
import CommissionManagementPage from './pages/CommissionManagementPage';
import FileUploadPage from './pages/FileUploadPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import PropertiesPage from './pages/PropertiesPage';
import AboutPage from './pages/AboutPage';
import EcumenicalLandingPage from './pages/EcumenicalLandingPage';

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAuth = false }) => {
  // For now, we'll assume authentication is handled by the RealtorContext
  // You can implement proper authentication logic here
  const { currentUser, loading } = useRealtorContext(); // Use context for authentication status
  const isAuthenticated = !!currentUser; // Check if currentUser exists

  if (loading) {
    return <div>Loading authentication...</div>; // Or a loading spinner
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" replace />; // Redirect to login page
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary>
      <RealtorProvider>
        <Router>
          <div className="App">
            <Toaster
              position="top-right"
              reverseOrder={false}
              toastOptions={{
                success: {
                  style: {
                    background: '#D1FAE5', // Tailwind green-100
                    color: '#065F46', // Tailwind green-800
                  },
                },
                error: {
                  style: {
                    background: '#FEE2E2', // Tailwind red-100
                    color: '#991B1B', // Tailwind red-800
                  },
                },
              }}
            />
            <Routes>
            {/* Ecumenical Master Landing Page - Default */}
            <Route path="/" element={<EcumenicalLandingPage />} />
            <Route path="/ecumenical" element={<EcumenicalLandingPage />} />
            
            {/* Unique Solar Garden Estate Routes */}
            <Route path="/solar-estate" element={<HomePage />} />
            <Route path="/solar-estate/home" element={<HomePage />} />
            <Route path="/solar-estate/contact" element={<ContactPage />} />
            <Route path="/solar-estate/properties" element={<PropertiesPage />} />
            <Route path="/solar-estate/properties/:id" element={<PropertiesPage />} />
            <Route path="/solar-estate/about" element={<AboutPage />} />
            <Route path="/solar-estate/location" element={<HomePage />} />
            <Route path="/solar-estate/facilities" element={<HomePage />} />
            <Route path="/solar-estate/payment" element={<HomePage />} />
            
            {/* Legacy routes for backward compatibility */}
            <Route path="/home" element={<HomePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/properties" element={<PropertiesPage />} />
            <Route path="/properties/:id" element={<PropertiesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/location" element={<HomePage />} />
            <Route path="/facilities" element={<HomePage />} />
            <Route path="/payment" element={<HomePage />} />

            {/* Authentication Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RealtorOnboardingPage />} />
            <Route path="/signup" element={<RealtorOnboardingPage />} />
            <Route path="/affiliate/register" element={<AffiliateRegisterPage />} />
            
            {/* Protected Realtor Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireAuth={true}>
                  <RealtorDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/affiliate/management"
              element={
                <ProtectedRoute requireAuth={true}>
                  <AffiliateManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/affiliate/invite"
              element={
                <ProtectedRoute requireAuth={true}>
                  <InviteAffiliatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/commission"
              element={
                <ProtectedRoute requireAuth={true}>
                  <CommissionManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/files"
              element={
                <ProtectedRoute requireAuth={true}>
                  <FileUploadPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/affiliate-management"
              element={
                <ProtectedRoute requireAuth={true}>
                  <AffiliateManagementPage />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Routes (if needed) */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAuth={true}>
                  <RealtorDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/realtors"
              element={
                <ProtectedRoute requireAuth={true}>
                  <RealtorDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/affiliates"
              element={
                <ProtectedRoute requireAuth={true}>
                  <AffiliateManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/commissions"
              element={
                <ProtectedRoute requireAuth={true}>
                  <CommissionManagementPage />
                </ProtectedRoute>
              }
            />
            
            {/* Profile Routes */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute requireAuth={true}>
                  <RealtorDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/edit"
              element={
                <ProtectedRoute requireAuth={true}>
                  <RealtorOnboardingPage />
                </ProtectedRoute>
              }
            />
            
            {/* Settings Routes */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute requireAuth={true}>
                  <RealtorDashboardPage />
                </ProtectedRoute>
              }
            />
            
            {/* Reports Routes */}
            <Route
              path="/reports"
              element={
                <ProtectedRoute requireAuth={true}>
                  <CommissionManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/sales"
              element={
                <ProtectedRoute requireAuth={true}>
                  <CommissionManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/commissions"
              element={
                <ProtectedRoute requireAuth={true}>
                  <CommissionManagementPage />
                </ProtectedRoute>
              }
            />
            
            {/* Marketing Routes */}
            <Route
              path="/marketing"
              element={
                <ProtectedRoute requireAuth={true}>
                  <FileUploadPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketing/materials"
              element={
                <ProtectedRoute requireAuth={true}>
                  <FileUploadPage />
                </ProtectedRoute>
              }
            />
            
            {/* Help & Support Routes */}
            <Route path="/help" element={<ContactPage />} />
            <Route path="/support" element={<ContactPage />} />
            <Route path="/faq" element={<ContactPage />} />
            
            {/* Legal Routes */}
            <Route path="/terms" element={<ContactPage />} />
            <Route path="/privacy" element={<ContactPage />} />
            
            {/* API Routes (for future API endpoints) */}
            <Route path="/api/*" element={<Navigate to="/" replace />} />
            
            {/* Catch-all route - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          <DebugInfo />
        </Router>
      </RealtorProvider>
    </ErrorBoundary>
  );
}

export default App;